import { useState, useMemo } from "react";
import {
  Plus,
  Edit2,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  X,
  Coins,
  Wallet,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Landmark,
  Bitcoin,
} from "lucide-react";
import { toast } from "sonner";
import { makeRequest } from "@/utils/makeRequest";
import Loader from "@/components/loader/Loader";
import type { Token } from "@/interfaces/token";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ErrorState } from "@/components/ErrorState";
import { useNoPaginationTokens } from "@/hooks";
import { Spinner } from "@/components/ui/spinner";
import { queryClient } from "@/services/queryClient";
import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";

type VerificationAction = "create" | "update";

const AdminTokens = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [selectedTokenIds, setSelectedTokenIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [loading, setLoading] = useState(false);
  const [pendingTokenPayload, setPendingTokenPayload] = useState<any>(null);

  const [verificationAction, setVerificationAction] =
    useState<VerificationAction | null>(null);

  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [tokenToToggle, setTokenToToggle] = useState<Token | null>(null);

  const itemsPerPage = 9;

  const navigate = useNavigate();

  const toggleSelection = (id: string) => {
    setSelectedTokenIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const selectAll = () => {
    setSelectedTokenIds(new Set(filteredTokens.map((t) => t.id)));
  };

  const clearSelection = () => {
    setSelectedTokenIds(new Set());
  };

  // Form state (kept intact)
  const [formData, setFormData] = useState({
    code: "",
    iso: "",
    iso2: "",
    symbol: "",
    name: "",
    country: "",
    address: "",
    decimals: "18",
    isFiat: false,
    icon: "",
    price: "",
    isActive: true,
  });

  const {
    data: tokenData,
    isLoading,
    isError,
    refetch,
  } = useNoPaginationTokens();

  const tokens: Token[] = tokenData ?? [];

  const isBulkEdit = selectedTokenIds.size > 0;

  const handleAddToken = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.code.trim()) {
      toast.error("Token code is required.");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Token name is required.");
      return;
    }

    if (!formData.symbol.trim()) {
      toast.error("Token symbol is required.");
      return;
    }

    if (!formData.iso.trim()) {
      toast.error("ISO code is required.");
      return;
    }

    if (!formData.address.trim()) {
      toast.error("Token contract address is required.");
      return;
    }

    const decimals = Number(formData.decimals);

    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 36) {
      toast.error("Token decimals must be a valid integer.");
      return;
    }

    const payload = {
      code: formData.code.trim().toUpperCase(),
      iso: formData.iso.trim().toUpperCase(),

      // IMPORTANT:
      // Don't send null because your Zod schema uses .optional()
      ...(formData.iso2.trim()
        ? { iso2: formData.iso2.trim().toUpperCase() }
        : {}),

      name: formData.name.trim(),
      symbol: formData.symbol.trim().toUpperCase(),

      ...(formData.country.trim() ? { country: formData.country.trim() } : {}),

      address: formData.address.trim().toLowerCase(),
      decimals,
      isFiat: formData.isFiat,

      ...(formData.icon.trim() ? { icon: formData.icon.trim() } : {}),
    };

    // Store the token until verification succeeds
    setPendingTokenPayload(payload);

    // Tell the verification handler this is a CREATE operation
    setVerificationAction("create");

    // Open OTP/security-code dialog
    setShowVerificationDialog(true);
  };

  const toggleToken = async (payload: {
    tokenId?: string;
    tokenIds?: string[];
    otp: string;
    securityCode: string;
  }) => {
    const res = await makeRequest.put("/token", payload);

    return res.data;
  };

  const handleVerificationSuccess = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    setLoading(true);

    try {
      if (verificationAction === "create") {
        if (!pendingTokenPayload) {
          throw new Error("Token creation data is missing.");
        }

        const res = await makeRequest.post("/token", {
          ...pendingTokenPayload,
          otp,
          securityCode,
        });

        const result = res.data;

        toast.success("Token Created", {
          description: `${
            result?.token?.symbol ?? pendingTokenPayload.symbol
          } was created and added to existing wallets.`,
        });
      } else if (verificationAction === "update") {
        await toggleToken({
          tokenId: tokenToToggle?.id,
          tokenIds:
            selectedTokenIds.size > 0 ? [...selectedTokenIds] : undefined,
          otp,
          securityCode,
        });

        toast.success("Token(s) updated successfully.");
      } else {
        throw new Error("Unknown verification action.");
      }

      await queryClient.invalidateQueries({
        queryKey: ["tokens:no-pagination"],
      });

      setShowVerificationDialog(false);

      setPendingTokenPayload(null);
      setVerificationAction(null);

      clearSelection();
      setTokenToToggle(null);
      setSelectedToken(null);
      setShowEditModal(false);

      setShowAddModal(false);

      setFormData({
        code: "",
        iso: "",
        iso2: "",
        symbol: "",
        name: "",
        country: "",
        address: "",
        decimals: "18",
        isFiat: false,
        icon: "",
        price: "",
        isActive: true,
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Operation failed.";

      toast.error("Operation Failed", {
        description: Array.isArray(message) ? message.join(", ") : message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditToken = (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (!selectedToken && selectedTokenIds.size === 0) {
      toast.error("Select at least one token.");
      return;
    }

    // setTokenToToggle(selectedToken);
    setShowVerificationDialog(true);
  };

  const filteredTokens = useMemo(() => {
    return tokens.filter((token) => {
      const q = searchQuery.toLowerCase();

      const matchesSearch =
        token.code?.toLowerCase().includes(q) ||
        token.name?.toLowerCase().includes(q) ||
        token.iso?.toLowerCase().includes(q) ||
        token.symbol?.toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? token.isActive : !token.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [tokens, searchQuery, statusFilter]);

  const totalPages = Math.ceil(filteredTokens.length / itemsPerPage);

  const paginatedTokens = filteredTokens.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const activeTokens = tokens.filter((t) => t.isActive).length;

  const inActiveTokens = tokens.filter((t) => !t.isActive).length;

  const handleExport = () => {
    const csvContent = [
      ["Code", "Name", "ISO", "Symbol", "Address", "Balance", "Status"].join(
        ",",
      ),
      ...filteredTokens.map((t) =>
        [
          t.code,
          t.name,
          t.iso,
          t.symbol,
          t.address,
          t.balance,
          t.isActive ? "active" : "inactive",
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `tokens-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();

    toast.success("Export Complete", {
      description: `${filteredTokens.length} tokens exported.`,
    });
  };

  const openEditModal = (token: Token) => {
    clearSelection();

    setSelectedToken(token);
    setTokenToToggle(token);

    setFormData({
      code: token.code ?? "",
      iso: token.iso ?? "",
      iso2: token.iso2 ?? "",
      symbol: token.symbol ?? "",
      name: token.name ?? "",
      country: token.country ?? "",
      address: token.address ?? "",
      decimals: String(token.decimals ?? 18),
      isFiat: token.isFiat ?? false,
      icon: token.icon ?? "",
      price: "",
      isActive: token.isActive ?? true,
    });

    setShowEditModal(true);
  };

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );
  if (isLoading) return <Loader type="bounce" />;

  return (
    <div className="min-h-screen bg-background px-1 lg:px-6">
      <div className="max-w-7xl mx-auto">
        <Button
          className="hidden lg:flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* HEADER (UNCHANGED) */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Token Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage platform tokens and their settings
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-card text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>

            <button
              type="button"
              onClick={() => {
                setSelectedToken(null);
                setTokenToToggle(null);

                setFormData({
                  code: "",
                  iso: "",
                  iso2: "",
                  symbol: "",
                  name: "",
                  country: "",
                  address: "",
                  decimals: "18",
                  isFiat: false,
                  icon: "",
                  price: "",
                  isActive: true,
                });

                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:brightness-110 transition-all"
            >
              <Plus className="w-4 h-4" />
              Add Token
            </button>
          </div>
        </div>

        {/* STATS (UNCHANGED UI) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Coins className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {tokens.length}
                </p>
                <p className="text-xs text-muted-foreground">Total Tokens</p>
              </div>
            </div>
          </div>

          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {activeTokens}
                </p>
                <p className="text-xs text-muted-foreground">Active Tokens</p>
              </div>
            </div>
          </div>

          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <XCircle className="w-4 h-4 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {inActiveTokens}
                </p>
                <p className="text-xs text-muted-foreground">Inactive Tokens</p>
              </div>
            </div>
          </div>

          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-violet-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {tokens.length}
                </p>
                <p className="text-xs text-muted-foreground">Network Tokens</p>
              </div>
            </div>
          </div>
        </div>

        {/* FILTERS (UNCHANGED) */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search tokens..."
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <Button variant="outline" onClick={selectAll}>
            Select All
          </Button>

          <Button
            variant="secondary"
            disabled={selectedTokenIds.size === 0}
            onClick={() => {
              setTokenToToggle(null);
              setSelectedToken(null);
              setShowEditModal(true);
            }}
          >
            Update Selected
            {selectedTokenIds.size > 0 && ` (${selectedTokenIds.size})`}
          </Button>

          <Button
            variant="ghost"
            disabled={selectedTokenIds.size === 0}
            onClick={clearSelection}
          >
            Clear
          </Button>
        </div>

        {/* GRID (UNCHANGED STRUCTURE) */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedTokens.map((token) => (
            <div
              key={token.id}
              className={`relative nex-card p-6 transition-all duration-200 ${
                token.isActive
                  ? "hover:border-primary/30 hover:shadow-md"
                  : "opacity-60 grayscale-[20%] border-dashed bg-muted/20"
              }`}
            >
              {!token.isActive && (
                <div className="absolute top-4 right-4 rounded-full bg-gray-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Disabled
                </div>
              )}

              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedTokenIds.has(token.id)}
                    onChange={() => toggleSelection(token.id)}
                    className="mt-1"
                  />

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-foreground">
                        {token.code}
                      </h3>

                      <span
                        className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          token.iso2
                            ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                            : "bg-violet-500/10 text-violet-500"
                        }`}
                      >
                        {token.iso2 ? "FIAT" : "CRYPTO"}
                      </span>
                    </div>

                    <p className="mt-1 text-sm text-muted-foreground">
                      {token.name}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => openEditModal(token)}
                  className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
              </div>

              {/* Divider */}
              <div className="my-5 border-t border-border" />

              {/* Details */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Symbol</span>

                  <span className="font-medium text-foreground">
                    {token.symbol}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Balance</span>

                  <span className="font-mono font-semibold text-foreground">
                    {Number(token.balance).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 8,
                    })}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Network</span>

                  <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-semibold">
                    {token.blockchainNetwork.name}
                  </span>
                </div>

                {token.iso2 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Country
                    </span>

                    <span className="font-medium text-foreground">
                      {token.iso2}
                    </span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    token.isActive
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "bg-gray-500/10 text-gray-500"
                  }`}
                >
                  {token.isActive ? "Active" : "Inactive"}
                </span>

                <span className="text-xs text-muted-foreground">
                  {token.iso2 ? "Fiat Asset" : "Crypto Asset"}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* PAGINATION (UNCHANGED) */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-muted-foreground">
              Showing {paginatedTokens.length} of {filteredTokens.length}
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="p-2 bg-card rounded-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                className="p-2 bg-card rounded-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleAddToken}>
              <div className="flex justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-foreground">
                    Add Token
                  </h3>

                  <p className="text-sm text-muted-foreground mt-1">
                    Add an ERC-20 token to BNB Smart Chain and provision it to
                    existing wallets.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="rounded-lg p-2 hover:bg-muted"
                  disabled={loading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Token Code
                  </label>

                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        code: e.target.value,
                      })
                    }
                    placeholder="ECO"
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Token Name
                  </label>

                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        name: e.target.value,
                      })
                    }
                    placeholder="Eco"
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  />
                </div>

                {/* Symbol */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Symbol
                  </label>

                  <input
                    type="text"
                    value={formData.symbol}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        symbol: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="ECO"
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  />
                </div>

                {/* ISO */}
                <div>
                  <label className="block text-sm font-medium mb-1">ISO</label>

                  <input
                    type="text"
                    value={formData.iso}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        iso: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="ECO"
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  />
                </div>

                {/* ISO2 */}
                <div>
                  <label className="block text-sm font-medium mb-1">ISO2</label>

                  <input
                    type="text"
                    maxLength={2}
                    value={formData.iso2}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        iso2: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="Optional — e.g. NG"
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Country
                  </label>

                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        country: e.target.value,
                      })
                    }
                    placeholder="Optional"
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  />
                </div>

                {/* Contract address */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    BNB Smart Chain Contract Address
                  </label>

                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        address: e.target.value,
                      })
                    }
                    placeholder="0x..."
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg font-mono text-sm"
                    disabled={loading}
                  />

                  <p className="text-xs text-muted-foreground mt-1">
                    Existing ERC-20 contract on BNB Smart Chain.
                  </p>
                </div>

                {/* Decimals */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Decimals
                  </label>

                  <input
                    type="number"
                    min={0}
                    max={36}
                    value={formData.decimals}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        decimals: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  />
                </div>

                {/* Token type */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Token Type
                  </label>

                  <select
                    value={formData.isFiat ? "fiat" : "crypto"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isFiat: e.target.value === "fiat",
                      })
                    }
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  >
                    <option value="crypto">Crypto Token</option>

                    <option value="fiat">
                      Fiat-backed / Platform Currency
                    </option>
                  </select>
                </div>

                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Icon URL
                  </label>

                  <input
                    type="url"
                    value={formData.icon}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        icon: e.target.value,
                      })
                    }
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Warning */}
              <div className="mt-5 rounded-lg border border-primary/20 bg-primary/5 p-4">
                <p className="text-sm font-medium text-foreground">
                  Wallet provisioning
                </p>

                <p className="text-xs text-muted-foreground mt-1">
                  Creating this token will automatically create an asset for the
                  token on the existing BNB address of every eligible wallet and
                  create the appropriate ledger accounts.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 bg-primary text-primary-foreground py-2.5 rounded-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Spinner />
                    Creating Token...
                  </div>
                ) : (
                  "Create Token"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4">
            <form onSubmit={handleEditToken}>
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-bold text-foreground">
                  Edit Token
                </h3>
                <button type="button" onClick={() => setShowEditModal(false)}>
                  <X />
                </button>
              </div>

              <div className="space-y-3">
                <input
                  className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                  value={formData.symbol}
                  readOnly
                  onChange={(e) =>
                    setFormData({ ...formData, symbol: e.target.value })
                  }
                />

                <input
                  className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                  value={formData.name}
                  readOnly
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />

                <input
                  className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                  value={formData.country}
                  readOnly
                  onChange={(e) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                />

                <select
                  className="w-full px-3 py-2 bg-card border border-border rounded-lg"
                  value={formData.isActive ? "true" : "false"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isActive: e.target.value === "true",
                    })
                  }
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-4 bg-primary text-primary-foreground py-2 rounded-lg disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center gap-1 text-center">
                    <Spinner />
                    Updating...
                  </div>
                ) : (
                  "Save Changes"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      <AdminVerificationDialog
        open={showVerificationDialog}
        onOpenChange={(open) => {
          setShowVerificationDialog(open);

          if (!open) {
            setTokenToToggle(null);
            setPendingTokenPayload(null);
            setVerificationAction(null);
          }
        }}
        onSuccess={handleVerificationSuccess}
      />
    </div>
  );
};

export default AdminTokens;
